import React, { useEffect, useState } from "react";
import { User,Camera,Save,Building2,Phone,Mail,MapPin,Briefcase,ChevronRight,Check,AlertCircle,Loader2
} from "lucide-react";
import { clsx } from "clsx";
import planApi from '@api/planApi';

const ROLE_LABELS = {
  R0: "시스템 관리자",
  R2: "일반 사용자/수행요원",
};

export function ProfileSettings() {
  const [form, setForm] = useState({
    memberId: "",
    memberNm: "",
    birthDt: "",
    genderCd: "M",
    phone1: "010",
    phone2: "",
    phone3: "",
    email: "",
    zipcode: "",
    address1: "",
    address2: "",
    bizRegNo: "",
    departmentNm: "",
    positionNm: "",
    roleId: "",
    staffId: "",
    staffNm: "",
    regDtm: "", // 🌟 백엔드에서 내려준다면 표시, 아니면 생략 가능
    modDtm: ""
  });

  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [activeTab, setActiveTab] = useState("personal");

  // 🌟 컴포넌트 마운트 시 내 정보 불러오기
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await planApi.getMyInfo();
        // 백엔드에서 받은 카멜 케이스 데이터를 폼 상태에 세팅
        setForm({
          memberId: res.memberId || "",
          memberNm: res.memberNm || "",
          birthDt: res.birthDt || "",
          genderCd: res.genderCd || "M",
          phone1: res.phone1 || "010",
          phone2: res.phone2 || "",
          phone3: res.phone3 || "",
          email: res.email || "",
          zipcode: res.zipcode || "",
          address1: res.address1 || "",
          address2: res.address2 || "",
          bizRegNo: res.bizRegNo || "",
          departmentNm: res.departmentNm || "",
          positionNm: res.positionNm || "",
          roleId: res.roleId || "",
          staffId: res.staffId || "",
          staffNm: res.staffNm || "",
          regDtm: res.regDtm || "-", // 등록일시 (DTO에 추가했다면)
          modDtm: res.modDtm || "-"  // 수정일시
        });
      } catch (e) {
        alert("정보를 불러오지 못했습니다.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInfo();
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // 🌟 저장 로직 (백엔드로 PUT 요청)
  const handleSave = async () => {
    if (!form.memberNm) {
      alert("회원명은 필수 입력입니다.");
      return;
    }

    setSaveStatus("saving");
    try {
      await planApi.updateMyInfo(form);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (e) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2500);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  // 🌟 (핵심) Daum 우편번호 팝업창 호출 함수
  const openPostcodePopup = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert("주소 검색 스크립트를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    new window.daum.Postcode({
      oncomplete: function(data) {
        let fullAddress = data.address; 
        let extraAddress = ''; 

        if (data.addressType === 'R') {
          if (data.bname !== '') { extraAddress += data.bname; }
          if (data.buildingName !== '') {
            extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
          }
          fullAddress += (extraAddress !== '' ? ` (${extraAddress})` : '');
        }

        // 상태 업데이트
        setForm(prev => ({
          ...prev,
          zipcode: data.zonecode,
          address1: fullAddress,
          address2: '' // 새로운 주소 검색 시 상세주소 초기화
        }));
      }
    }).open(); // 🌟 .open()을 호출하면 별도의 작은 팝업창이 알아서 뜹니다!
  };

  const tabs = [
    { id: "personal", label: "개인 정보", icon: User },
    { id: "contact", label: "연락처 · 주소", icon: MapPin },
    { id: "work", label: "직장 정보", icon: Briefcase },
  ];

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
          <span>홈</span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-600 font-medium">프로필 설정</span>
        </div>
        <h2 className="text-xl font-bold text-slate-800">프로필 설정</h2>
        <p className="text-sm text-slate-500 mt-0.5">개인 정보 및 연락처를 수정하고 저장합니다.</p>
      </div>

      {/* Profile Card */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Cover Banner */}
        <div className="h-28 bg-gradient-to-r from-slate-800 via-blue-900 to-indigo-900 relative">
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }}
          />
        </div>

        <div className="px-6 pb-5">
          {/* Avatar + Basic Info */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 -mt-10 mb-5">
            <div className="relative self-start shrink-0">
              <div className="h-20 w-20 rounded-2xl border-4 border-white bg-slate-800 flex items-center justify-center shadow-lg overflow-hidden">
                <span className="text-2xl font-bold text-white">
                    {/* 회원 이름이 있으면 첫 글자, 없으면 'U' 표시 */}
                    {form.memberNm ? form.memberNm[0] : "U"} 
                </span>
              </div>
              <button className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white shadow-md hover:bg-blue-700 transition-colors border-2 border-white">
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex-1 min-w-0 sm:pt-11">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-bold text-slate-800">{form.memberNm || "사용자"}</h3>
                <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                  {ROLE_LABELS[form.roleId] ?? form.roleId}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {form.departmentNm || "부서 미지정"} · {form.positionNm || "직책 미지정"} &nbsp;|&nbsp; ID: <span className="font-mono text-slate-600">{form.memberId}</span>
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-100 mb-6">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                  activeTab === id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab: Personal Info */}
          {activeTab === "personal" && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {/* memberId - read only */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">회원 ID <span className="text-slate-300">(변경 불가)</span></label>
                  <input type="text" value={form.memberId} readOnly className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-400 cursor-not-allowed" />
                </div>
                {/* memberNm */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">회원명 <span className="text-red-400">*</span></label>
                  <input type="text" value={form.memberNm} onChange={(e) => set("memberNm", e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors" />
                </div>
                {/* birthDt */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">생년월일</label>
                  <input type="date" value={form.birthDt || ""} onChange={(e) => set("birthDt", e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors" />
                </div>
                {/* genderCd */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">성별</label>
                  <div className="flex gap-3 h-10 items-center">
                    {[
                      { value: "M", label: "남성" },
                      { value: "F", label: "여성" },
                    ].map(({ value, label }) => (
                      <label key={value} className={clsx("flex flex-1 h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-all", form.genderCd === value ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400")}>
                        <input type="radio" name="gender" value={value} checked={form.genderCd === value} onChange={() => set("genderCd", value)} className="sr-only" />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Read-only metadata (등록/수정 일시) */}
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 grid grid-cols-2 gap-4">
                {[
                  { label: "등록 일시", value: form.regDtm },
                  { label: "최종 수정 일시", value: form.modDtm },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                    <p className="text-sm font-medium text-slate-600">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab: Contact & Address */}
          {activeTab === "contact" && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* Phone */}
              <div>
                <label className="flex text-xs font-semibold text-slate-500 mb-1.5 items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> 전화번호</label>
                <div className="flex items-center gap-2">
                  <select value={form.phone1} onChange={(e) => set("phone1", e.target.value)} className="h-10 w-24 rounded-lg border border-slate-300 bg-white px-3 text-sm text-center text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                     <option value="010">010</option>
                     <option value="02">02</option>
                     <option value="031">031</option>
                     <option value="032">032</option>
                  </select>
                  <span className="text-slate-300 font-bold">—</span>
                  <input type="text" value={form.phone2} onChange={(e) => set("phone2", e.target.value.replace(/[^0-9]/g, ''))} maxLength={4} placeholder="0000" className="h-10 w-24 rounded-lg border border-slate-300 bg-white px-3 text-sm text-center text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <span className="text-slate-300 font-bold">—</span>
                  <input type="text" value={form.phone3} onChange={(e) => set("phone3", e.target.value.replace(/[^0-9]/g, ''))} maxLength={4} placeholder="0000" className="h-10 w-24 rounded-lg border border-slate-300 bg-white px-3 text-sm text-center text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <span className="text-sm text-slate-400 hidden sm:inline ml-2">→ {form.phone1}-{form.phone2}-{form.phone3}</span>
                </div>
              </div>

              {/* EMAIL */}
              <div>
                <label className="flex text-xs font-semibold text-slate-500 mb-1.5 items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> 전자메일주소</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="example@atos.co.kr" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>

              {/* Address */}
              <div>
                <label className="flex text-xs font-semibold text-slate-500 mb-1.5 items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> 주소</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={form.zipcode} onChange={(e) => set("zipcode", e.target.value.replace(/[^0-9]/g, ''))} maxLength={5} placeholder="우편번호" className="h-10 w-36 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    <button type="button" 
                    onClick={openPostcodePopup}
                    className="h-10 px-4 rounded-lg border border-slate-300 bg-slate-50 text-sm text-slate-600 hover:bg-slate-100 transition-colors">
                        우편번호 검색
                    </button>
                  </div>
                  <input type="text" value={form.address1} onChange={(e) => set("address1", e.target.value)} placeholder="기본주소" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <input type="text" value={form.address2} onChange={(e) => set("address2", e.target.value)} placeholder="상세주소" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Work Info */}
          {activeTab === "work" && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">부서명</label>
                  <input type="text" value={form.departmentNm} onChange={(e) => set("departmentNm", e.target.value)} placeholder="예: 안전관리팀" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">직책명</label>
                  <input type="text" value={form.positionNm} onChange={(e) => set("positionNm", e.target.value)} placeholder="예: 팀장" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">사업자등록번호</label>
                  <input type="text" value={form.bizRegNo} onChange={(e) => set("bizRegNo", e.target.value)} placeholder="000-00-00000" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">수행요원 이름 <span className="text-slate-300">(관리자 지정)</span></label>
                  <input type="text" value={form.staffNm || "미지정"} readOnly className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-400 cursor-not-allowed" />
                </div>
              </div>

              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">현재 역할</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {ROLE_LABELS[form.roleId] ?? form.roleId} <span className="ml-2 text-xs font-normal text-slate-400">({form.roleId})</span>
                  </p>
                </div>
                <p className="ml-auto text-xs text-slate-400 hidden sm:block">역할 변경은 관리자에게 문의하세요</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Footer */}
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-6 py-4 shadow-sm">
        <p className="text-xs text-slate-400">* 표시 항목은 필수 입력 사항입니다.</p>
        <div className="flex items-center gap-3">
          {saveStatus === "saved" && (
            <div className="flex items-center gap-1.5 text-sm text-green-600 animate-in fade-in duration-200">
              <Check className="h-4 w-4" /> 저장되었습니다
            </div>
          )}
          {saveStatus === "error" && (
            <div className="flex items-center gap-1.5 text-sm text-red-500 animate-in fade-in duration-200">
              <AlertCircle className="h-4 w-4" /> 저장 실패
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saveStatus === "saving"}
            className={clsx(
              "flex h-10 items-center gap-2 rounded-lg px-6 text-sm font-medium text-white shadow-sm transition-all",
              saveStatus === "saving" ? "bg-blue-400 cursor-wait" : "bg-blue-600 hover:bg-blue-700"
            )}
          >
            {saveStatus === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saveStatus === "saving" ? "저장 중..." : "변경 사항 저장"}
          </button>
        </div>
      </div>
    </div>
  );
}